import React, { useState, useEffect, useMemo } from "react";
import { useBosAutoCompletion } from "../../components/BosAutoCompletion";
import { DefaultWidgetContextMenu } from "../../components/DefaultWidgetContextMenu";
import { FloatingLabelContainer } from "../../components/FloatingLabel";
import { safeParseJSONString, isUiEvent } from "../logic/WidgetUtils";

// Externals
import cn from "classnames";

// DevExtreme components
import { Autocomplete, Button as AutoCompleteButton } from 'devextreme-react/autocomplete';

// Component styles
import classes from "./EntityPickerWidget.module.scss";

const EMPTY = undefined;

export function EntityPickerWidget(props) {
  const { ui, data, config, writeBack, startWork, doAction, getContextActions, getAutocompleteSuggestions, useFloatingLabel } = props;

  const [localContent, setLocalContent] = useState(EMPTY);
  const [hasFocus, setHasFocus] = useState(false);

  let widgetId = ui.id;
  let autocompleteId = config ? config.AutocompleteId : null;
  let showSelectionPicker = config ? config.AllowSelectionPicker : null;
  let allowNewEntries = config ? config.AllowNewEntries : null;
  let newEntryActionId = config ? config.NewEntryActionId : null;
  let content = localContent !== EMPTY ? localContent : data && data.content ? safeParseJSONString(data.content) : null;
  let contentDisplayData = content ? content.DisplayData : "";
  let disabled = ui.readonly || (data && data.readonly);

  const searchButtonOptionsMemo = useMemo(() => {
    return {
      icon: 'search',
      stylingMode: 'text',
      //disabled: false,
      onClick: async () => {
        await doAction(config.ActionId, content ? content.ItemId : null);
      }
    };
  }, [config, content]);

  const { store: autoCompletionStore } = useBosAutoCompletion(autocompleteId, getAutocompleteSuggestions);

  useEffect(() => {
    //console.log("EntityPickerWidget data of " + ui.id + ": ", data);
    setLocalContent(EMPTY);
  }, [data]);

  const setContent = (args) => {
    if (!isUiEvent(args)) return;
    //console.log("EntityPickerWidget setContent", args);
    setLocalContent({ DisplayData: args.value, Value: null });
  };

  const setSelection = (args) => {
    if (!hasItemId(args)) return;
    //console.log("EntityPickerWidget setSelection", args);
    setLocalContent({ DisplayData: args.selectedItem.displayData, Value: args.selectedItem.itemId });
    writeBack(args.selectedItem.itemId.toString());
  };

  const setNewItem = async (args) => {
    if (hasItemId(args)) return;
    if (!allowNewEntries) return;
    //console.log("EntityPickerWidget setNewItem", contentDisplayData);
    await doAction(newEntryActionId, contentDisplayData);
    setLocalContent(EMPTY);
  };

  const assureValidContent = () => {
    let id = (content && content.Value) ? content.Value : "";
    //console.log("EntityPickerWidget assureValidContent", id);
    if (id) return;
    if (allowNewEntries) {
      doAction(newEntryActionId, contentDisplayData);
      setLocalContent(EMPTY);
    }
    else {
      setLocalContent({ DisplayData: null, Value: null });
      writeBack("");
    }
  };

  const handleOnCopy = () => {
    return JSON.stringify(content);
  };

  const handleOnPaste = (value) => {
    let newContent = safeParseJSONString(value);
    if (!(newContent && newContent.Value)) return;
    setLocalContent(newContent);
    writeBack(newContent.Value.toString(), true);
  };

  const handleOnDelete = () => {
    setLocalContent({ DisplayData: null, Value: null });
    writeBack(null, true);
  };

  const hasItemId = (args) => {
    return (args && args.selectedItem && args.selectedItem.itemId);
  };

  return (
    <DefaultWidgetContextMenu
      widgetId={widgetId}
      getContextActions={getContextActions}
      onCopy={handleOnCopy}
      onPaste={handleOnPaste}
      onDelete={handleOnDelete}
      editable={!disabled}
    >
      <FloatingLabelContainer condition={useFloatingLabel} widgetId={widgetId} hasContent={content} hasFocus={hasFocus} text={ui.label}>
        <Autocomplete
          className={cn(useFloatingLabel ? classes.withFloatingLabel : "", disabled ? classes.disabled : "")}
          value={contentDisplayData}
          dataSource={autoCompletionStore}
          valueExpr="displayData"
          searchMode="startswith"
          onKeyUp={() => startWork()}
          onValueChanged={e => setContent(e)}
          onFocusOut={(e) => { setHasFocus(false); assureValidContent(); }}
          onFocusIn={(e) => setHasFocus(true)}
          onSelectionChanged={e => setSelection(e)}
          onEnterKey={e => setNewItem(e)}
          itemRender={(data) => <span>{data.displayData}</span>}
          disabled={disabled}
          //inputAttr={{disabled: autocompleteId === null}}
          height={ui.controlHeight + "em"}
        >
          {showSelectionPicker
            ? (
              <AutoCompleteButton
                name="searchButton"
                location="after"
                options={searchButtonOptionsMemo}
              />
            )
            : null
          }
        </Autocomplete>
      </FloatingLabelContainer>
    </DefaultWidgetContextMenu>
  );
}

